import type { ChannelVariant, SportsChannel, SportsMatch, SportsToday } from '../account/api';

/**
 * One entry per channel in a match's lists. The provider lists each channel
 * once per quality ("BEIN SPORTS 1 HD", "BEIN SPORTS 1 FHD", "BEIN SPORTS 1 4K"),
 * and the relay tests each one, so a match showed the same channel up to four
 * times. Here they become one entry; the player's quality menu switches
 * between them (as in Live TV, #100).
 */

/** Words that only describe the picture (same list as the relay's quality-variants.ts). */
const QUALITY_WORDS =
  /\b(8k|4k|uhd|fhd|full ?hd|hd|sd|lq|hq|low|2160p|1080p|720p|576p|480p|360p|raw|hevc|h\.?265|x265|h\.?264|\d{2,3} ?fps|\d+k exclusive)\b|ᵁᴴᴰ|ᴿᴬᵂ|ᶠᴴᴰ|ᴴᴰ|ˢᴰ|ᴴᴱⱽᶜ|\d{2,3}ᶠᵖˢ/gi;

/**
 * The channel a name is about, whatever its quality and word order: "TSN1 FHD" and "TSN 1 HD" give
 * the same, and so do "BEIN SPORTS 1 ENGLISH HD [AR]" and "BEIN SPORTS ENGLISH 1 4K [AR]".
 */
export function channelFamily(name: string): string {
  const words = name
    .replace(QUALITY_WORDS, ' ')
    .replace(/(^|[^0-9])0+(?=[0-9])/g, '$1')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    // "+" stays with its word: "Sky Sports" and "Sky Sports+" are different channels.
    .match(/[a-z\u0600-\u06ff]+\+*|[0-9]+\+*/g);
  return words ? [...words].sort().join(' ') : '';
}

/** The name without its quality words, for an entry that stands for several qualities. */
export function plainChannelName(name: string): string {
  const plain = name
    .replace(QUALITY_WORDS, ' ')
    .replace(/\(\s*\)|\[\s*\]/g, ' ')
    .replace(/\s*[|/-]\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
  return plain || name.trim();
}

const RAW = /\bRAW\b|ᴿᴬᵂ/i;
const HEVC = /\bhevc\b|\bh\.?265\b|\bx265\b|ᴴᴱⱽᶜ/i;

/** As the player's menu names it: "4K", "FHD", "HD", "SD", "RAW", "" (the provider's name says none). */
export function qualityText(channel: Pick<SportsChannel, 'label' | 'quality'>): string {
  if (RAW.test(channel.label)) return 'RAW';
  const label = channel.quality === 'UHD' ? '4K' : (channel.quality ?? '');
  return HEVC.test(channel.label) ? `${label} HEVC`.trim() : label;
}

/** Which quality plays by default, as in Live TV: HD, FHD, unmarked, SD, 4K; RAW and HEVC last (most browsers can't play them). */
function defaultRank(channel: SportsChannel): number {
  if (RAW.test(channel.label) || HEVC.test(channel.label)) return 6;
  return ({ HD: 0, FHD: 1, '': 2, SD: 3, '4K': 4 } as Record<string, number>)[qualityText(channel)] ?? 5;
}

/** Best picture first in the menu. */
const BEST_FIRST: Record<string, number> = { '4K': 0, FHD: 1, HD: 2, '': 3, SD: 4, RAW: 5 };

/** A match's channels with each channel once; its qualities are in `variants` (best picture first). */
export function groupChannelQualities(channels: SportsChannel[]): SportsChannel[] {
  const groups = new Map<string, SportsChannel[]>();
  channels.forEach((channel, index) => {
    const family = channelFamily(channel.label);
    // A name that is only quality words can't be matched safely: kept alone.
    const key = family ? `${channel.language ?? ''}\u0000${family}` : `\u0001${index}`;
    const group = groups.get(key);
    if (group) group.push(channel);
    else groups.set(key, [channel]);
  });
  return [...groups.values()].map((members) => {
    if (members.length === 1) return members[0]!;
    const shown = members.reduce((best, channel) => (defaultRank(channel) < defaultRank(best) ? channel : best));
    const variants = members
      .map((channel) => ({ streamId: channel.streamId, label: channel.label, quality: qualityText(channel) }))
      .sort((a, b) => (BEST_FIRST[a.quality] ?? 6) - (BEST_FIRST[b.quality] ?? 6));
    // Two copies in one quality (the provider's backups): "HD", "HD 2".
    const seen = new Map<string, number>();
    for (const variant of variants) {
      const count = (seen.get(variant.quality) ?? 0) + 1;
      seen.set(variant.quality, count);
      if (count > 1) variant.quality = `${variant.quality} ${count}`.trim();
    }
    const watching = members.reduce((sum, channel) => sum + (channel.watching ?? 0), 0);
    return {
      ...shown,
      label: plainChannelName(shown.label),
      // Shown with its best picture ("4K"), as the list is ordered by it.
      quality: members.reduce<SportsChannel['quality']>((best, channel) => (rankOf(channel.quality) < rankOf(best) ? channel.quality : best), shown.quality),
      // Confirmed by viewers when any of its qualities is.
      confirmed: members.some((channel) => channel.confirmed) || undefined,
      // Shared with another match only when every quality is.
      shared: members.every((channel) => channel.shared === true),
      ...(watching > 0 ? { watching } : {}),
      variants,
    };
  });
}

function rankOf(quality: SportsChannel['quality']): number {
  return quality === null ? 4 : { UHD: 0, FHD: 1, HD: 2, SD: 3 }[quality];
}

/** A channel a match will be (or might be) on; `variants` set here when it has several qualities. */
export type Upcoming = NonNullable<SportsMatch['upcoming']>[number] & { variants?: ChannelVariant[] };

/** The channels a match will be on (or might be on), each channel once. */
export function groupUpcomingQualities(upcoming: Upcoming[]): Upcoming[] {
  const playable = upcoming.filter((channel): channel is Upcoming & { streamId: number } => typeof channel.streamId === 'number');
  const grouped = groupChannelQualities(
    playable.map((channel) => ({ streamId: channel.streamId, label: channel.label, logo: channel.logo ?? null, language: channel.language, quality: channel.quality ?? null, shared: channel.shared })),
  );
  const byStream = new Map(grouped.map((channel) => [channel.streamId, channel]));
  const seen = new Set<string>();
  const result: Upcoming[] = [];
  for (const channel of upcoming) {
    if (typeof channel.streamId === 'number') {
      const entry = byStream.get(channel.streamId);
      if (!entry) continue;
      result.push({ ...channel, label: entry.label, quality: entry.quality, ...(entry.variants ? { shared: entry.shared, variants: entry.variants } : {}) });
      continue;
    }
    // Names only (before kick-off): the same channel once.
    const family = channelFamily(channel.label);
    const key = `${channel.language ?? ''}\u0000${family}`;
    if (family && seen.has(key)) continue;
    seen.add(key);
    result.push(family && upcoming.some((other) => other !== channel && typeof other.streamId !== 'number' && channelFamily(other.label) === family && other.language === channel.language) ? { ...channel, label: plainChannelName(channel.label) } : channel);
  }
  return result;
}

/** Today's matches with every channel listed once. */
export function groupMatchQualities(today: SportsToday): SportsToday {
  return {
    ...today,
    matches: today.matches.map((match) => ({
      ...match,
      channels: groupChannelQualities(match.channels),
      ...(match.upcoming ? { upcoming: groupUpcomingQualities(match.upcoming) } : {}),
    })),
  };
}
